import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getUiLang } from '../utils/lang';
import axios from 'axios';
import { useCompare } from '../context/CompareContext';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { Scale, X, ShoppingCart, Lock } from 'lucide-react';
import StarRating from '../components/StarRating';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ComparePage = () => {
  const { t, i18n } = useTranslation();
  const { productIds, remove, clear } = useCompare();
  const { isAuthenticated } = useAuth();
  const { addToCart, currency, formatPrice } = useCart();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await Promise.all(
          productIds.map((id) => axios.get(`${API}/products/${id}`).then((r) => r.data).catch(() => null))
        );
        if (!cancelled) setProducts(res.filter(Boolean));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [productIds]);

  const getName = (p) => p?.translations?.[getUiLang(i18n)]?.name || p?.translations?.sk?.name || p?.sku;
  const getPrice = (p) => (currency === 'EUR' ? p.price_eur : p.price_usd);

  const specRows = [
    { key: 'light_transmission', labelKey: 'compare.spec_light_transmission' },
    { key: 'uv_protection', labelKey: 'compare.spec_uv_protection' },
    { key: 'thickness', labelKey: 'compare.spec_thickness' },
    { key: 'roll_size', labelKey: 'compare.spec_roll_size' },
    { key: 'color', labelKey: 'compare.spec_color' },
    { key: 'adhesive_type', labelKey: 'compare.spec_adhesive_type' },
    { key: 'warranty', labelKey: 'compare.spec_warranty' },
  ];

  return (
    <div className="container mx-auto px-4 py-8" data-testid="compare-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Scale className="h-7 w-7 text-orange-500" />
          <h1 className="text-3xl font-bold">{t('compare.title')}</h1>
          <span className="text-gray-500">({products.length})</span>
        </div>
        {products.length > 0 && (
          <button
            onClick={clear}
            className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition"
            data-testid="compare-clear-btn"
          >
            {t('compare.clear_all')}
          </button>
        )}
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">{t('common.loading')}</div>
      ) : products.length === 0 ? (
        <div className="text-center py-16 bg-gray-50 rounded-lg" data-testid="compare-empty">
          <Scale className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 text-lg mb-4">{t('compare.empty')}</p>
          <Link
            to="/catalog"
            className="inline-block px-6 py-3 bg-orange-500 text-white rounded-lg font-semibold hover:bg-orange-600 transition"
          >
            {t('wishlist.browse_catalog')}
          </Link>
        </div>
      ) : (
        <div className="overflow-x-auto bg-white rounded-lg shadow">
          <table className="w-full">
            <thead>
              <tr className="border-b">
                <th className="p-4 text-left bg-gray-50 sticky left-0 z-10 min-w-[180px]">
                  {t('compare.parameter')}
                </th>
                {products.map((p) => (
                  <th key={p.id} className="p-4 min-w-[220px]" data-testid={`compare-col-${p.id}`}>
                    <div className="relative flex flex-col items-center gap-2">
                      <button
                        onClick={() => remove(p.id)}
                        className="absolute -top-2 -right-2 p-1 bg-white border border-gray-200 rounded-full hover:bg-red-100 transition"
                        title={t('compare.remove')}
                        data-testid={`compare-remove-${p.id}`}
                      >
                        <X className="h-3.5 w-3.5 text-red-500" />
                      </button>
                      <Link to={`/product/${p.id}`} className="block w-32 h-32 bg-gray-100 rounded overflow-hidden">
                        {p.images?.[0] ? (
                          <img src={p.images[0]} alt={getName(p)} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-300 text-xs">
                            No image
                          </div>
                        )}
                      </Link>
                      <Link to={`/product/${p.id}`} className="font-semibold text-sm text-center hover:text-orange-500 transition line-clamp-2">
                        {getName(p)}
                      </Link>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr className="border-b">
                <td className="p-4 font-medium bg-gray-50 sticky left-0">{t('compare.price')}</td>
                {products.map((p) => (
                  <td key={p.id} className="p-4 text-center">
                    {isAuthenticated ? (
                      <span className="text-lg font-bold">{formatPrice(getPrice(p))}</span>
                    ) : (
                      <span className="flex items-center gap-1 justify-center text-gray-500 text-sm">
                        <Lock className="h-3.5 w-3.5" />
                        {t('catalog.login_for_price')}
                      </span>
                    )}
                  </td>
                ))}
              </tr>
              <tr className="border-b">
                <td className="p-4 font-medium bg-gray-50 sticky left-0">{t('compare.rating')}</td>
                {products.map((p) => (
                  <td key={p.id} className="p-4 text-center">
                    {p.rating_count > 0 ? (
                      <div className="flex justify-center">
                        <StarRating value={p.rating_avg} size="h-4 w-4" count={p.rating_count} />
                      </div>
                    ) : (
                      <span className="text-gray-400 text-sm">—</span>
                    )}
                  </td>
                ))}
              </tr>
              <tr className="border-b">
                <td className="p-4 font-medium bg-gray-50 sticky left-0">{t('compare.stock')}</td>
                {products.map((p) => (
                  <td key={p.id} className="p-4 text-center">
                    {p.stock > 1 ? (
                      <span className="text-green-600 font-medium">{
                        p.unit === 'mb'
                          ? t('product.in_stock_mb', { count: p.stock })
                          : t('product.in_stock_pcs', { count: p.stock })
                      }</span>
                    ) : p.stock === 1 ? (
                      <span className="text-yellow-600">{t('product.check_with_seller_short')}</span>
                    ) : (
                      <span className="text-red-500">{t('product.on_order')}</span>
                    )}
                  </td>
                ))}
              </tr>
              {specRows.map(({ key, labelKey }) => (
                <tr key={key} className="border-b">
                  <td className="p-4 font-medium bg-gray-50 sticky left-0">{t(labelKey)}</td>
                  {products.map((p) => (
                    <td key={p.id} className="p-4 text-center">
                      {p.specifications?.[key] ? (
                        <span>{p.specifications[key]}</span>
                      ) : (
                        <span className="text-gray-400 text-sm">—</span>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
              <tr>
                <td className="p-4 font-medium bg-gray-50 sticky left-0">{t('compare.actions')}</td>
                {products.map((p) => (
                  <td key={p.id} className="p-4 text-center">
                    {p.stock > 1 && isAuthenticated ? (
                      <button
                        onClick={() => addToCart(p, 1)}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition text-sm"
                        data-testid={`compare-add-cart-${p.id}`}
                      >
                        <ShoppingCart className="h-4 w-4" />
                        {t('product.add_to_cart')}
                      </button>
                    ) : (
                      <Link
                        to={`/product/${p.id}`}
                        className="inline-block px-4 py-2 border border-orange-500 text-orange-500 rounded-lg hover:bg-orange-50 transition text-sm"
                      >
                        {t('product.view_details')}
                      </Link>
                    )}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ComparePage;
